// Per-device preferences in localStorage under `zooi:*` (design/plan.md appendix B).
// Storage can be unavailable (private windows, blocked site data); the app
// then simply forgets, it never fails.

export function readPref(key: string): string | null {
  try {
    return localStorage.getItem(`zooi:${key}`);
  } catch {
    return null;
  }
}

export function writePref(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(`zooi:${key}`);
    else localStorage.setItem(`zooi:${key}`, value);
  } catch {
    // Not saved; the choice still applies until the page reloads.
  }
}

/** "system" follows the operating system's "increase contrast" setting. */
export type Contrast = "system" | "standard" | "high";

export const contrast = (): Contrast => {
  const v = readPref("contrast");
  return v === "standard" || v === "high" ? v : "system";
};

/** Sets the attribute styles.css keys on: absent = follow the system. */
export function applyContrast(value: Contrast = contrast()) {
  const root = document.documentElement;
  if (value === "system") root.removeAttribute("data-contrast");
  else root.setAttribute("data-contrast", value === "high" ? "high" : "standard");
}

export function setContrast(value: Contrast) {
  writePref("contrast", value === "system" ? null : value);
  applyContrast(value);
}

/** "system" follows the operating system's light or dark appearance (design system §2). */
export type Theme = "system" | "light" | "dark";

export const theme = (): Theme => {
  const v = readPref("theme");
  return v === "light" || v === "dark" ? v : "system";
};

/** What the page shows for a preference; styles.css keys on the resolved value only. */
export const resolveTheme = (value: Theme, systemDark: boolean): "light" | "dark" =>
  value === "system" ? (systemDark ? "dark" : "light") : value;

const darkQuery = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null);
let following: (() => void) | null = null;

/** Sets <html data-theme>, and keeps following the system while the preference is "system". */
export function applyTheme(value: Theme = theme()) {
  const query = darkQuery();
  const set = () => document.documentElement.setAttribute("data-theme", resolveTheme(value, Boolean(query?.matches)));
  following?.();
  following = null;
  set();
  if (value === "system" && query) {
    query.addEventListener("change", set);
    following = () => query.removeEventListener("change", set);
  }
}

export function setTheme(value: Theme) {
  writePref("theme", value === "system" ? null : value);
  applyTheme(value);
}
